import { sanitizeUrl } from '../api/urlSanitizer';
import { debugEnabled } from '../lib/log';

// Playback startup trace (Flutter `PlaybackStartupTrace`,
// utils/playback_startup_trace.dart:16-266; spec 11 §11; DATA-021, alt:
// `performance.mark`). One trace per play attempt records the first time
// each marker happens, in ms since T0, as `performance.mark`s named
// `playback:<id>:<marker>` (visible in the browser's performance panel), and
// prints a one-line summary when the attempt ends, in debug builds or with
// `?debug`. The last 12 attempts stay available for diagnostics.

export type StartupMarker =
  | 'T0.play_tap'
  | 'T1.ui_buffering'
  | 'T2.source_ready'
  | 'T3.preflight_start'
  | 'T3.preflight_end'
  | 'T4.set_audio_source_called'
  | 'T5.player_buffering'
  | 'T5.player_ready'
  | 'T5.player_playing'
  | 'T6.first_position';

export type StartupOutcome = 'success' | 'cancelled' | 'error';
export type PreflightResult = 'ok' | `http_${number}` | 'timeout' | 'error';

export interface PlaybackStartupSnapshot {
  attemptId: string;
  itemId: string;
  trigger: string;
  /** Epoch ms of T0. */
  startedAt: number;
  /** Marker → ms since T0 (first occurrence). */
  markers: Partial<Record<StartupMarker, number>>;
  /** Named preflight stages (session, local file, progress…) → result and duration. */
  preflight: Record<string, { result: PreflightResult; ms: number }>;
  source?: { kind: 'stream' | 'local' | 'direct'; url?: string };
  outcome?: StartupOutcome;
  detail?: string;
  totalMs?: number;
}

const HISTORY_LIMIT = 12;
const history: PlaybackStartupSnapshot[] = [];
let attempts = 0;

const clock = (): number => (typeof performance !== 'undefined' ? performance.now() : Date.now());

function mark(name: string): void {
  try {
    performance.mark(name);
  } catch {
    // performance.mark is missing or full: the snapshot still has the timing.
  }
}

export class PlaybackStartupTrace {
  readonly attemptId: string;
  private readonly t0 = clock();
  private readonly snapshotData: PlaybackStartupSnapshot;
  private readonly preflightStarts = new Map<string, number>();
  private done = false;

  constructor(itemId: string, trigger: string) {
    this.attemptId = `${Date.now().toString(36)}-${++attempts}`;
    this.snapshotData = { attemptId: this.attemptId, itemId, trigger, startedAt: Date.now(), markers: {}, preflight: {} };
    history.push(this.snapshotData);
    if (history.length > HISTORY_LIMIT) history.shift();
    this.mark('T0.play_tap');
  }

  get finished(): boolean {
    return this.done;
  }

  private markName(marker: string): string {
    return `playback:${this.attemptId}:${marker}`;
  }

  /** Records `marker` the first time it happens. */
  mark(marker: StartupMarker): void {
    if (this.done || this.snapshotData.markers[marker] !== undefined) return;
    this.snapshotData.markers[marker] = Math.round(clock() - this.t0);
    mark(this.markName(marker));
  }

  /** The audio source is known (its URL is recorded without credentials). */
  sourceReady(kind: 'stream' | 'local' | 'direct', url?: string): void {
    if (this.done) return;
    this.snapshotData.source = { kind, ...(url && !url.startsWith('blob:') ? { url: sanitizeUrl(url) } : {}) };
    this.mark('T2.source_ready');
  }

  /** Times one named preflight stage around `work`, recording how it ended. */
  async preflight<T>(name: string, work: () => Promise<T>): Promise<T> {
    if (this.done) return work();
    this.mark('T3.preflight_start');
    this.preflightStarts.set(name, clock());
    try {
      const value = await work();
      this.endPreflight(name, 'ok');
      return value;
    } catch (err) {
      const status = (err as { status?: unknown })?.status;
      const timedOut = (err as { isTimeout?: unknown })?.isTimeout === true || (err as Error)?.name === 'TimeoutError';
      this.endPreflight(name, timedOut ? 'timeout' : typeof status === 'number' && status > 0 ? `http_${status}` : 'error');
      throw err;
    }
  }

  private endPreflight(name: string, result: PreflightResult): void {
    const started = this.preflightStarts.get(name);
    if (this.done || started === undefined) return;
    this.snapshotData.preflight[name] = { result, ms: Math.round(clock() - started) };
    this.mark('T3.preflight_end');
  }

  /** Ends the attempt (only the first call counts) and prints the summary in debug builds. */
  finish(outcome: StartupOutcome, detail?: string): void {
    if (this.done) return;
    this.done = true;
    const data = this.snapshotData;
    data.outcome = outcome;
    data.totalMs = Math.round(clock() - this.t0);
    if (detail) data.detail = detail;
    try {
      performance.measure(`playback:${this.attemptId}`, { start: this.markName('T0.play_tap') });
    } catch {
      // ignore
    }
    if (debugEnabled()) {
      try {
        console.debug(formatStartupSummary(data));
      } catch {
        // ignore
      }
    }
  }

  snapshot(): PlaybackStartupSnapshot {
    return structuredCloneSafe(this.snapshotData);
  }
}

function structuredCloneSafe<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

const SHORT: Array<[StartupMarker, string]> = [
  ['T1.ui_buffering', 't1'],
  ['T2.source_ready', 't2'],
  ['T4.set_audio_source_called', 't4'],
  ['T5.player_buffering', 't5b'],
  ['T5.player_ready', 't5r'],
  ['T5.player_playing', 't5p'],
  ['T6.first_position', 't6'],
];

/** `[playback][id] done success=true t1=… t2=… … total=… preflight={session:ok/312ms,…}` (Flutter's summary line). */
export function formatStartupSummary(data: PlaybackStartupSnapshot): string {
  const parts = SHORT.filter(([marker]) => data.markers[marker] !== undefined).map(([marker, label]) => `${label}=${data.markers[marker]}`);
  const preflight = Object.entries(data.preflight)
    .map(([name, p]) => `${name}:${p.result}/${p.ms}ms`)
    .join(',');
  return [
    `[playback][${data.attemptId}] done success=${data.outcome === 'success'}`,
    `outcome=${data.outcome ?? 'running'}`,
    `item=${data.itemId}`,
    `trigger=${data.trigger}`,
    ...(data.source ? [`source=${data.source.kind}`] : []),
    ...parts,
    `total=${data.totalMs ?? 0}`,
    `preflight={${preflight}}`,
    ...(data.detail ? [`detail=${JSON.stringify(data.detail)}`] : []),
  ].join(' ');
}

/** The last attempts, oldest first (Flutter `playbackStartupHistory`). */
export function playbackStartupHistory(): PlaybackStartupSnapshot[] {
  return history.map(structuredCloneSafe);
}
